import { N_ } from '../i18n/i18n';
/**
 * design/gdd/tutorial.md — everything a new delver is told, in order.
 *
 * Pure data. No DOM, no network, no engine imports, so the whole script can
 * be read as prose in one file and asserted on in tests: that the copy states
 * the rule rather than naming the button, that every anchor is a selector the
 * screen actually renders, and that the two chapters cover the mechanics a
 * player cannot recover from getting wrong.
 *
 * ## Why steps advance on the action, not on "Next"
 *
 * A tutorial that is nine plaques and a Next button is a slideshow, and a
 * slideshow is read the way a EULA is read. Wherever the game already has a
 * real action for what the step is teaching — opening Contracts, spending a
 * talent point, swinging, insuring an item — the step waits for that action
 * and takes it as the answer. `advanceOn` names the signal; `nextLabel` is
 * only for the steps that genuinely have nothing to do but understand.
 *
 * Every step still carries a Next, because a signal can be missed: the player
 * had already opened Contracts before the step appeared, a click landed on
 * the tile's icon, a phone lost the pointer event. Waiting forever on a
 * trigger that will not come is the one failure mode a tutorial must not have.
 *
 * ## Why the delve chapter is contextual and not a scripted level
 *
 * There is no tutorial dungeon and there should not be one: this is an
 * extraction game, and a run with nothing at stake teaches the opposite of
 * the thing. The delve steps hang off signals a real run emits anyway — you
 * moved, you swung, something entered your bag — so the chapter plays in
 * whatever dungeon the player actually queued into, at the pace they play it.
 */

export type TutorialChapter = 'hub' | 'delve';

/**
 * What ends a step.
 *
 * `next` — nothing to do; the plaque explains and the player acknowledges.
 * Everything else is a signal the surrounding screen already had, forwarded
 * to the runner by name. Adding a trigger means emitting the name, never
 * teaching this file about the engine.
 */
export type TutorialSignal =
  // hub
  | 'character:created'
  | 'talents:opened'
  | 'panel:contracts'
  | 'panel:insurance'
  | 'panel:shop'
  | 'panel:play'
  // delve
  | 'run:moved'
  | 'run:attacked'
  | 'run:ability'
  /** 2026-10-16 — the player chose a target themselves (Tab, the Target plate, F1–F4 or a party frame). */
  | 'run:targeted'
  | 'run:looted'
  | 'run:insured'
  | 'run:extract-known';

export interface TutorialStep {
  id: string;
  chapter: TutorialChapter;
  /** The small line above the heading — where you are, not what to do. */
  eyebrow: string;
  title: string;
  /** One or two sentences. The rule, not the control. */
  body: string;
  /**
   * CSS selector for the thing being talked about, or null to sit in the
   * middle of the screen. Missing from the DOM is not an error — the step
   * still shows, centred, which is what happens on a narrow layout where the
   * anchor is behind a scroll.
   */
  anchor: string | null;
  /** The signal that completes it, or 'next' for acknowledge-only. */
  advanceOn: TutorialSignal | 'next';
  /** The acknowledge button's label. Shown on every step; see the file comment. */
  nextLabel: string;
  /**
   * Held back until this signal has fired at least once.
   *
   * The difference between "do this" and "here is what just happened". A step
   * that explains carried loot has nothing to explain until something is in
   * the bag, and showing it on spawn would be teaching a rule about an object
   * the player has never seen.
   */
  showAfter?: TutorialSignal;
  /**
   * Skipped entirely when this returns false — evaluated each time the runner
   * looks for the next step, so a condition can become true mid-chapter.
   */
  when?: (ctx: TutorialContext) => boolean;
}

/** What the runner knows about the player when it picks the next step. */
export interface TutorialContext {
  /** Delvers on the account. Zero changes the first hub step from a tour into a task. */
  characterCount: number;
  /** Runs of cover left. At zero the insurance step says the thing that costs money to learn. */
  insuranceCharges: number;
  /** Fine pointer — the delve chapter's controls differ, and telling a phone about WASD is worse than saying nothing. */
  hasKeyboard: boolean;
  /**
   * Playing a healer this run (2026-10-16) — the ally-heal step is only true
   * for them. Optional: absent reads as "no", so the hub never needs to know.
   */
  isHealer?: boolean;
}

export const HUB_STEPS: readonly TutorialStep[] = [
  {
    id: 'welcome',
    chapter: 'hub',
    eyebrow: N_('The Sconce'),
    title: N_('This is the room between runs'),
    body: N_('You go down carrying what you choose, and you keep only what you carry back out. Everything in this room exists to change those odds.'),
    anchor: null,
    advanceOn: 'next',
    nextLabel: N_('Show me'),
  },
  {
    id: 'create-delver',
    chapter: 'hub',
    eyebrow: N_('Your roster'),
    title: N_('Make a delver'),
    body: N_('A class, a name, and the arms they carry. Three are free; the roster is the account’s, not a single run’s — a delver who dies keeps their levels and loses their bag.'),
    anchor: '[data-tile="characters"]',
    advanceOn: 'character:created',
    nextLabel: N_('Later'),
    when: (ctx) => ctx.characterCount === 0,
  },
  {
    id: 'delver',
    chapter: 'hub',
    eyebrow: N_('Your delver'),
    title: N_('The one who goes down'),
    body: N_('Levels, talents and gear stay with them between runs. Everything in their bag does not.'),
    anchor: '#hub-stage',
    advanceOn: 'next',
    nextLabel: N_('Next'),
    when: (ctx) => ctx.characterCount > 0,
  },
  {
    id: 'talents',
    chapter: 'hub',
    eyebrow: N_('Your delver'),
    title: N_('Talents are the build'),
    body: N_('Every level pays a point, and the tree is what makes two knights different. A point is not spent until you spend it — nothing here is lost by waiting.'),
    anchor: '#hub-talents-btn',
    advanceOn: 'talents:opened',
    nextLabel: N_('Next'),
    when: (ctx) => ctx.characterCount > 0,
  },
  {
    id: 'loadout',
    chapter: 'hub',
    eyebrow: N_('Your delver'),
    title: N_('What goes down with you'),
    body: N_('Arms in hand and charges in the satchel. This strip is the manifest; Inventory is where you change it.'),
    anchor: '#hub-loadout',
    advanceOn: 'next',
    nextLabel: N_('Next'),
    when: (ctx) => ctx.characterCount > 0,
  },
  {
    id: 'energy',
    chapter: 'hub',
    eyebrow: N_('The cost of going down'),
    title: N_('Energy gates the door'),
    body: N_('One delve spends one Energy. It refills on its own over the day, and the header plate says when the next one lands.'),
    anchor: '[data-top-up="energy"]',
    advanceOn: 'next',
    nextLabel: N_('Next'),
  },
  {
    id: 'contracts',
    chapter: 'hub',
    eyebrow: N_('Before you queue'),
    title: N_('A contract picks the map'),
    body: N_('It names where you delve and what finishing it pays. Without one you take the Cellars, which cost nothing and pay accordingly.'),
    anchor: '[data-tile="contracts"]',
    advanceOn: 'panel:contracts',
    nextLabel: N_('Next'),
  },
  {
    id: 'insurance',
    chapter: 'hub',
    eyebrow: N_('Before you queue'),
    title: N_('Death costs you the bag'),
    body: N_('Insured items are the exception, and they are the only one. Cover lasts a single run — spend it, and the next delve needs another.'),
    anchor: '[data-tile="insurance"]',
    advanceOn: 'panel:insurance',
    nextLabel: N_('Next'),
  },
  {
    id: 'shop',
    chapter: 'hub',
    eyebrow: N_('Between runs'),
    title: N_('Sell the haul, buy the next one'),
    body: N_('Wax is what the dungeon pays you. Ember is bought, and it never buys power directly — only skins, slots and Wax at a capped rate.'),
    anchor: '[data-tile="shop"]',
    advanceOn: 'panel:shop',
    nextLabel: N_('Next'),
  },
  {
    id: 'play',
    chapter: 'hub',
    eyebrow: N_('When you are ready'),
    title: N_('Go down'),
    body: N_('Pick a mode and a floor, then hold Play. The rest of this happens underground.'),
    anchor: '#hub-play-btn',
    advanceOn: 'panel:play',
    nextLabel: N_('Not yet'),
  },
];

export const DELVE_STEPS: readonly TutorialStep[] = [
  {
    id: 'move',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Move'),
    body: N_('WASD walks. Shift sprints, Ctrl crouches, Space jumps. Click once to hand the mouse to the camera; Esc gives it back.'),
    anchor: null,
    advanceOn: 'run:moved',
    nextLabel: N_('Got it'),
    when: (ctx) => ctx.hasKeyboard,
  },
  {
    id: 'strike',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Swing, and read the swing coming back'),
    body: N_('Left mouse attacks; J, K and L choose the side. F parries — timed, not held — and right mouse blocks while you hold it.'),
    anchor: null,
    advanceOn: 'run:attacked',
    nextLabel: N_('Got it'),
    when: (ctx) => ctx.hasKeyboard,
  },
  {
    id: 'abilities',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Your abilities'),
    // 2026-10-16: the old line said Tab "picks up the nearest target first",
    // which it did not — it skipped to the second nearest. It does now, but an
    // enemy ability also picks for you, so the rule worth stating is that.
    body: N_('Number keys or the slots fire them. With no target, an attack picks the nearest enemy you can see in front of you. Hover a slot, or hold it on a phone, to read what it does and how far it reaches.'),
    anchor: '[data-tutorial="action-bar"]',
    advanceOn: 'run:ability',
    nextLabel: N_('Got it'),
  },
  {
    id: 'target',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Choose what you hit'),
    body: N_('Tab steps through the enemies you can see, nearest first. The frame at the top names your target and how far away it is, and says when it is out of reach.'),
    anchor: null,
    advanceOn: 'run:targeted',
    showAfter: 'run:ability',
    nextLabel: N_('Got it'),
    when: (ctx) => ctx.hasKeyboard,
  },
  {
    id: 'target-touch',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Choose what you hit'),
    body: N_('Tap Target to step through the enemies you can see, nearest first. The frame at the top names your target and how far away it is, and says when it is out of reach.'),
    anchor: '[data-touch="target"]',
    advanceOn: 'run:targeted',
    showAfter: 'run:ability',
    nextLabel: N_('Got it'),
    when: (ctx) => !ctx.hasKeyboard,
  },
  {
    id: 'heal-ally',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Your heals find the wounded'),
    body: N_('A heal lands on the squadmate you picked. Pick one with F2 to F4 or by clicking their frame on the right; F1 is you. With nobody picked, it goes to whoever nearby is hurt the most.'),
    anchor: null,
    advanceOn: 'run:targeted',
    showAfter: 'run:ability',
    nextLabel: N_('Got it'),
    when: (ctx) => !!ctx.isHealer && ctx.hasKeyboard,
  },
  {
    id: 'heal-ally-touch',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Your heals find the wounded'),
    body: N_('A heal lands on the squadmate you picked. Tap their frame on the right to pick one. With nobody picked, it goes to whoever nearby is hurt the most.'),
    anchor: null,
    advanceOn: 'run:targeted',
    showAfter: 'run:ability',
    nextLabel: N_('Got it'),
    when: (ctx) => !!ctx.isHealer && !ctx.hasKeyboard,
  },
  {
    id: 'loot',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Carrying is not keeping'),
    body: N_('Everything in this bag is weight, and weight slows you down, and the bar shows how much is left. Press I to open or close it. None of it is yours until you are standing outside.'),
    anchor: '[data-tutorial="loot"]',
    advanceOn: 'next',
    showAfter: 'run:looted',
    nextLabel: N_('Understood'),
  },
  {
    id: 'insure-item',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('Mark what you cannot lose'),
    body: N_('An insured item survives your death; everything beside it does not. Mark it now — a body cannot change its mind.'),
    anchor: '[data-tutorial="loot"]',
    advanceOn: 'run:insured',
    showAfter: 'run:looted',
    nextLabel: N_('Not this one'),
    when: (ctx) => ctx.insuranceCharges > 0,
  },
  {
    id: 'extract',
    chapter: 'delve',
    eyebrow: N_('Underground'),
    title: N_('The only way to keep any of it'),
    body: N_('Follow the compass to the gate and hold E to crank it open. Then press E to climb out and keep what you found, or G to go deeper and stake it. Leaving is the whole game — dying one room short pays nothing.'),
    anchor: '[data-tutorial="compass"]',
    advanceOn: 'next',
    showAfter: 'run:extract-known',
    nextLabel: N_('Understood'),
  },
];

export const TUTORIAL_STEPS: readonly TutorialStep[] = [...HUB_STEPS, ...DELVE_STEPS];

/** The id whose completion means the whole thing is done. */
export const FINAL_STEP_ID = DELVE_STEPS[DELVE_STEPS.length - 1].id;

export function stepsFor(chapter: TutorialChapter): readonly TutorialStep[] {
  return chapter === 'hub' ? HUB_STEPS : DELVE_STEPS;
}

/**
 * The next step to show, or null when the chapter is done for this player.
 *
 * `when` is evaluated here rather than at the point of showing so a step that
 * has become irrelevant is not merely hidden but passed over — the runner asks
 * this again after every completion, so creating a delver mid-chapter drops
 * `create-delver` and picks up the tour at `delver`.
 */
export function nextStep(
  chapter: TutorialChapter,
  seen: readonly string[],
  ctx: TutorialContext,
  fired: ReadonlySet<TutorialSignal> = new Set(),
): TutorialStep | null {
  const done = new Set(seen);
  return stepsFor(chapter).find((s) => (
    !done.has(s.id)
    && (s.when?.(ctx) ?? true)
    && (!s.showAfter || fired.has(s.showAfter))
  )) ?? null;
}

/** How far through the chapter this step is, for the "3 of 9" line. Skipped steps do not count. */
export function stepPosition(
  step: TutorialStep, ctx: TutorialContext,
): { index: number; total: number } {
  const applicable = stepsFor(step.chapter).filter((s) => s.when?.(ctx) ?? true);
  return { index: applicable.indexOf(step) + 1, total: applicable.length };
}
